import ErrorModal from '@app/components/ErrorModal';
import LogoCarousel from '@app/components/logoCarousel/LogoCarousel';
import { showContentOnlyModal } from '@app/components/Modal';
import PageWithHeader from '@app/components/PageWithHeader';
import TransitionLoader from '@app/components/TransitionLoader';
import config from '@app/config';
import PoweredByLogo from '@app/images/powered-by.svg';
import nsknoxLogoBlack from '@app/images/nsknox-black.svg';
import SVG from '@app/components/SVG';
import styled from '@emotion/styled';
import {
  SupplierRegistrationProcessFormType,
  SupplierRegistrationProcessInstructionType,
  SupplierRegistrationProcessInstructionTypesSubsets,
} from '@app/domain/commonSupplierRegistration';
import useAppStores from '@app/hooks/useAppStores';
import useInfraStores from '@app/hooks/useInfraStores';
import useIsMounted from '@app/hooks/useIsMounted';
import useMountEffect from '@app/hooks/useMountEffect';
import useTheme from '@app/hooks/useTheme';
import Log from '@app/libs/logger';
import { EVENTS } from '@app/libs/analytics';
import useVendorPortalTrackEvent from '@supplierRegistration/hooks/useVendorPortalTrackEvent';
import { extractLogErrorIdFromError, isRequestError, isWebErrorContent } from '@app/libs/request';
import { HttpStatus } from '@app/libs/request/httpConsts';
import browserHistory from '@app/utils/browserHistory';
import BackButton from '@supplierRegistration/components/BackButton';
import { RegistrationError } from '@supplierRegistration/components/RegistrationError';
import { InactiveLinkError } from '@supplierRegistration/components/InactiveLinkError';
import {
  InstructionNextActionType,
  ReducedSupplierRegistrationProcess,
  SupplierRegistrationBankAccountFormFields,
  SupplierRegistrationCompanyInfoFormFields,
  SupplierRegistrationFormValuesState,
  SupplierRegistrationProgressStep,
  ValidationProcessSteps,
} from '@supplierRegistration/domain/supplierRegistration';
import { AsyncInitiateValidationMaskedData } from '@supplierRegistration/domain/supplierRegistrationAsyncInitiateValidationProcess';
import SupplierRegistrationBankAccountDetails from '@supplierRegistration/routes/supplierRegistrationProcess/validationSteps/SupplierRegistrationBankAccountDetails';
import SupplierRegistrationCompanyInformation from '@supplierRegistration/routes/supplierRegistrationProcess/validationSteps/SupplierRegistrationCompanyInformation';
import SupplierRegistrationEmailVerification from '@supplierRegistration/routes/supplierRegistrationProcess/validationSteps/SupplierRegistrationEmailVerification';
import SupplierRegistrationOpenBankingSelector from '@supplierRegistration/routes/supplierRegistrationProcess/validationSteps/SupplierRegistrationOpenBankingSelector';
import WelcomeSupplier from '@supplierRegistration/routes/supplierRegistrationProcess/validationSteps/WelcomeSupplier';
import {
  isSuitableForOpenBanking,
  isSuitableForOpenBankingAsync,
  validateFinicityAccountAsync,
} from '@supplierRegistration/services/openBankingService';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import {
  transformFormFieldsToSupplierRegistrationAsyncRequest,
  transformRegularValidationProcessRequest,
} from '@supplierRegistration/utils/transformers';
import {
  getDocumentSubmissionLimitErrorI18nKey,
  isSupplierValidationDocumentsRejectedResponse,
  mapDocumentFileErrorsToTypeAndIndex,
} from '@supplierRegistration/services/supplierRegistrationFormServices';
import { guestSession } from '@supplierRegistration/services/supplierRegistrationGuestSessionService';
import { observer } from 'mobx-react';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AboveCardSpace, BlueText, LtrText, PageContainer } from './SupplierRegistrationFormPage.styles';

const SupplierRegistrationFormPage: FC = observer(() => {
  const { navigationStore, languageStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationFormStore, supplierRegistrationCustomerDataStore, supplierRegistrationDocumentsStore } =
    useAppStores<SupplierRegistrationMode>();
  const { t } = useTranslation();
  const trackEvent = useVendorPortalTrackEvent();
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const isIpsMode = navigationStore.isIpsPage;
  const { brand } = useTheme<SupplierRegistrationMode>();
  const isMountedRef = useIsMounted();
  const customerName = supplierRegistrationCustomerDataStore.customerName;
  const additionalLogos = brand?.additionalLogos;
  const configuration = supplierRegistrationCustomerDataStore.currentConfiguration.result ?? null;
  const isConfigurationResolvedEmpty =
    supplierRegistrationCustomerDataStore.currentConfiguration.isResolved() &&
    !supplierRegistrationCustomerDataStore.currentConfiguration.result;
  const isAsyncProcessErrored = !!supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedDataLoadError;

  const enterEventFired = useRef(false);

  // Starts the guest session's context-scoped restore as early as the page itself mounts, well
  // before the supplier could ever click Continue on welcome -- so a returning, already-verified
  // supplier's click sees isVerified resolved and goes straight to companyInformation instead of
  // flashing the emailVerification step (which would otherwise still self-skip once init resolves).
  useMountEffect(() => {
    if (config.featureFlags?.supplierRegistrationDocuments) {
      void guestSession.init();
    }
  });

  // configuration loads asynchronously — we wait for it to resolve before firing
  // so that ref_id is populated. The ref guard ensures the event fires exactly once.
  useEffect(() => {
    if (enterEventFired.current || (!isConfigurationResolvedEmpty && !configuration)) return;
    enterEventFired.current = true;
    if (isConfigurationResolvedEmpty && !brand && !isIpsMode) {
      trackEvent({ name: EVENTS.VENDOR_PORTAL_BLOCKED_PAGE });
    } else {
      trackEvent({
        name: EVENTS.VENDOR_PORTAL_ENTER,
        is_brand_page: !!brand,
        is_ips_mode: isIpsMode,
        ref_id: configuration?.ref ?? undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigurationResolvedEmpty, configuration]);

  function getGoogleAnalyticsClientIdFromCookie(): string | null {
    const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));

    if (!gaCookie) {
      return null;
    }

    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      return parts.slice(-2).join('.');
    }

    return null;
  }

  function onSupplierDetailsFormSubmit(supplierValues: SupplierRegistrationCompanyInfoFormFields): void {
    saveSupplierDetailsValues(supplierValues);
    trackEvent({
      name: EVENTS.VENDOR_PORTAL_PAYEE_DETAILS_COMPLETED,
      country_code: supplierValues.country,
      ref_id: configuration?.ref ?? undefined,
      is_brand_page: !!brand,
      is_ips_mode: isIpsMode,
    });
  }

  function saveLoadedValues(values: SupplierRegistrationFormValuesState): void {
    supplierRegistrationFormStore.setSupplierRegistrationValues({
      supplierValues: values.supplierValues,
      accountValues: values.accountValues,
    });
    nextStep();
  }

  function saveSupplierDetailsValues(supplierValues: SupplierRegistrationCompanyInfoFormFields): void {
    supplierRegistrationFormStore.setSupplierRegistrationValues({
      supplierValues,
      accountValues: supplierRegistrationFormStore.currentSupplierRegistrationValues.accountValues,
    });
  }

  function saveAccountDetailsValues(newAccountValues: SupplierRegistrationBankAccountFormFields): void {
    const { supplierValues, accountValues } = supplierRegistrationFormStore.currentSupplierRegistrationValues;

    if (supplierValues) {
      supplierRegistrationFormStore.setSupplierRegistrationValues({
        supplierValues: supplierRegistrationFormStore.currentSupplierRegistrationValues.supplierValues,
        accountValues: { ...newAccountValues, hasAgreed: newAccountValues.hasAgreed ?? accountValues?.hasAgreed },
      });
    }
  }

  async function callAsyncPreRegistration(
    companyInfoValues: SupplierRegistrationCompanyInfoFormFields,
    bankAccountValues: SupplierRegistrationBankAccountFormFields,
    ref: string,
    asyncMaskedData: AsyncInitiateValidationMaskedData,
    captchaToken: string | undefined,
  ): Promise<void> {
    const payload = transformFormFieldsToSupplierRegistrationAsyncRequest(
      companyInfoValues,
      bankAccountValues,
      ref,
      asyncMaskedData,
      supplierRegistrationFormStore.isIndividual,
      null,
    );

    await supplierRegistrationFormStore.preRegistrationRequestFromAsyncInitiateValidation(
      payload,
      asyncMaskedData.id,
      captchaToken,
    );
  }

  async function callPreRegistrationRequest(
    companyInfoValues: SupplierRegistrationCompanyInfoFormFields,
    bankAccountValues: SupplierRegistrationBankAccountFormFields,
  ): Promise<void> {
    try {
      setIsRequestLoading(true);

      const shouldUseCaptcha = config.featureFlags?.supplierRegistrationCaptcha;

      const captchaToken = shouldUseCaptcha
        ? await supplierRegistrationCustomerDataStore.getCaptchaToken('pre-registration')
        : undefined;

      const currentAsyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData;

      if (currentAsyncProcessMaskedData?.isResolved() && configuration?.ref) {
        await callAsyncPreRegistration(
          companyInfoValues,
          bankAccountValues,
          configuration?.ref,
          currentAsyncProcessMaskedData.result,
          captchaToken,
        );
      } else {
        const payload = transformRegularValidationProcessRequest(
          companyInfoValues,
          bankAccountValues,
          isIpsMode,
          configuration,
          supplierRegistrationFormStore.isIndividual,
          null,
        );
        await supplierRegistrationFormStore.preRegistrationRequest(payload, captchaToken);
      }

      if (shouldUseCaptcha) {
        supplierRegistrationCustomerDataStore.getCaptchaRef()?.current?.reset();
      }
    } catch (e) {
      Log.exception(e);
    } finally {
      if (isMountedRef.current) {
        setIsRequestLoading(false);
      }
    }
  }

  async function onAccountDetailsFormSubmit(accountValuesToSave: SupplierRegistrationBankAccountFormFields): Promise<void> {
    saveAccountDetailsValues(accountValuesToSave);
    trackEvent({
      name: EVENTS.VENDOR_PORTAL_ACCOUNT_DETAILS_COMPLETED,
      account_country_code: accountValuesToSave.accountBankCountryCode,
      ref_id: configuration?.ref ?? undefined,
      is_brand_page: !!brand,
      is_ips_mode: isIpsMode,
    });
    await handleAccountDetailsSubmission(accountValuesToSave);
  }

  async function handleAccountDetailsSubmission(accountValuesToSave: SupplierRegistrationBankAccountFormFields): Promise<void> {
    const { supplierValues, accountValues } = supplierRegistrationFormStore.currentSupplierRegistrationValues;

    if (supplierValues && accountValues) {
      await callPreRegistrationRequest(supplierValues, accountValues);
    }

    if (config.featureFlags?.finicityOpenBanking && (await checkIsSuitableForOpenBanking())) {
      supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.useOpenBanking);

      return;
    }

    await storeValidationProcess();
  }

  async function storeValidationProcess(): Promise<boolean> {
    const { supplierValues, accountValues } = supplierRegistrationFormStore.currentSupplierRegistrationValues;

    if (supplierValues && accountValues) {
      return await storeValidationProcessRequest(supplierValues, accountValues);
    }

    return false;
  }

  function nextStep(): void {
    switch (supplierRegistrationFormStore.currentValidationProcessStep) {
      case ValidationProcessSteps.welcome:
        supplierRegistrationFormStore.setValidationProcessStep(
          config.featureFlags?.supplierRegistrationDocuments && !guestSession.isVerified
            ? ValidationProcessSteps.emailVerification
            : ValidationProcessSteps.companyInformation,
        );
        break;
      case ValidationProcessSteps.emailVerification:
        supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.companyInformation);
        break;
      case ValidationProcessSteps.companyInformation:
        supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.bankAccountDetails);
        break;
    }
  }

  function previousStep(): void {
    switch (supplierRegistrationFormStore.currentValidationProcessStep) {
      case ValidationProcessSteps.companyInformation:
        if (!supplierRegistrationFormStore.skipWelcome) {
          supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.welcome);
        }
        break;
      case ValidationProcessSteps.bankAccountDetails:
        supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.companyInformation);
        break;
      case ValidationProcessSteps.useOpenBanking:
        supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.bankAccountDetails);
        break;
    }
  }

  function renderBackButton(): ReactNode {
    const currentStep = supplierRegistrationFormStore.currentValidationProcessStep;

    if (
      currentStep === ValidationProcessSteps.bankAccountDetails ||
      (!supplierRegistrationFormStore.skipWelcome && currentStep === ValidationProcessSteps.companyInformation) ||
      currentStep === ValidationProcessSteps.useOpenBanking
    ) {
      return <BackButton onClick={previousStep}>{t('supplierValidation.supplierRegister.back')}</BackButton>;
    }
  }

  function getValidationStepComponent(step: ValidationProcessSteps): ReactNode {
    if (isAsyncProcessErrored) {
      return <RegistrationError customerName={customerName} />;
    }

    if (isConfigurationResolvedEmpty && !brand && !isIpsMode) {
      return <InactiveLinkError />;
    }

    const currentAsyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData;

    switch (step) {
      case ValidationProcessSteps.welcome:
        supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.Welcome);
        return (
          <WelcomeSupplier
            configuration={configuration}
            nextStep={nextStep}
            formValues={supplierRegistrationFormStore.currentSupplierRegistrationValues}
            saveValues={saveLoadedValues}
            isIpsMode={isIpsMode}
          />
        );
      case ValidationProcessSteps.emailVerification:
        // Shares the Welcome position on the header stepper -- this step has no dot of its own,
        // it's a gate between welcome and companyInformation, not a new leg of the journey.
        supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.Welcome);
        return (
          <SupplierRegistrationEmailVerification
            configuration={configuration}
            nextStep={nextStep}
            formValues={supplierRegistrationFormStore.currentSupplierRegistrationValues}
            isIpsMode={isIpsMode}
          />
        );
      case ValidationProcessSteps.companyInformation:
        supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.Registration);
        return (
          <SupplierRegistrationCompanyInformation
            configuration={configuration}
            nextStep={nextStep}
            onFormSubmit={onSupplierDetailsFormSubmit}
            formValues={supplierRegistrationFormStore.currentSupplierRegistrationValues}
            saveValues={saveSupplierDetailsValues}
            isIpsMode={isIpsMode}
          />
        );
      case ValidationProcessSteps.bankAccountDetails:
        supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.BankAccountDetails);
        return (
          <SupplierRegistrationBankAccountDetails
            configuration={configuration}
            nextStep={nextStep}
            onFormSubmit={onAccountDetailsFormSubmit}
            formValues={supplierRegistrationFormStore.currentSupplierRegistrationValues}
            saveValues={saveAccountDetailsValues}
            isIpsMode={isIpsMode}
            isIndividual={supplierRegistrationFormStore.isIndividual}
          />
        );
      case ValidationProcessSteps.useOpenBanking:
        supplierRegistrationFormStore.setProgressStep(SupplierRegistrationProgressStep.BankAccountDetails);
        return (
          <SupplierRegistrationOpenBankingSelector
            setLoading={setIsRequestLoading}
            onRegularSelect={storeValidationProcess}
            configuration={configuration}
            nextStep={nextStep}
            formValues={supplierRegistrationFormStore.currentSupplierRegistrationValues}
            isIpsMode={isIpsMode}
            validateFinicityAccountAsync={
              currentAsyncProcessMaskedData?.isResolved() && configuration?.ref ? validateFinicityAccountAsyncHandler : undefined
            }
          />
        );
    }
  }

  async function validateFinicityAccountAsyncHandler(customerId: string): Promise<void> {
    const { supplierValues, accountValues } = supplierRegistrationFormStore.currentSupplierRegistrationValues;

    if (!supplierValues || !accountValues) {
      throw new Error('Missing supplier or account values');
    }

    const shouldUseCaptcha = config.featureFlags?.supplierRegistrationCaptcha;

    const captchaToken = shouldUseCaptcha
      ? await supplierRegistrationCustomerDataStore.getCaptchaToken('finicity-validation')
      : undefined;

    const currentAsyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData;

    if (currentAsyncProcessMaskedData?.isResolved() && configuration?.ref) {
      const asyncInitiateValidationProcessRequest = transformFormFieldsToSupplierRegistrationAsyncRequest(
        supplierValues,
        accountValues,
        configuration?.ref,
        currentAsyncProcessMaskedData.result,
        supplierRegistrationFormStore.isIndividual,
        null,
      );

      await validateFinicityAccountAsync(
        asyncInitiateValidationProcessRequest,
        currentAsyncProcessMaskedData.result.id,
        customerId,
        captchaToken,
      );

      if (shouldUseCaptcha) {
        supplierRegistrationCustomerDataStore.getCaptchaRef()?.current?.reset();
      }
    } else {
      throw new Error('Missing async properties for async process');
    }
  }

  function getNextAction(displayInstructionType: SupplierRegistrationProcessInstructionType): InstructionNextActionType {
    return SupplierRegistrationProcessInstructionTypesSubsets.bankValidation.includes(displayInstructionType)
      ? InstructionNextActionType.paymentKnox
      : InstructionNextActionType.payee;
  }

  const checkIsSuitableForOpenBanking = async (): Promise<boolean> => {
    let isSuitable = false;
    try {
      setIsRequestLoading(true);

      const { supplierValues, accountValues } = supplierRegistrationFormStore.currentSupplierRegistrationValues;

      if (!supplierValues || !accountValues) {
        return false;
      }

      const currentAsyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData;

      if (currentAsyncProcessMaskedData?.isResolved() && configuration?.ref) {
        const asyncInitiateValidationProcessRequest = transformFormFieldsToSupplierRegistrationAsyncRequest(
          supplierValues,
          accountValues,
          configuration?.ref,
          currentAsyncProcessMaskedData.result,
          supplierRegistrationFormStore.isIndividual,
          null,
        );

        isSuitable = await isSuitableForOpenBankingAsync(
          asyncInitiateValidationProcessRequest,
          currentAsyncProcessMaskedData.result.id,
        );
      } else {
        const supplierRegistrationProcessRequest = transformRegularValidationProcessRequest(
          supplierValues,
          accountValues,
          isIpsMode,
          configuration,
          supplierRegistrationFormStore.isIndividual,
          null,
        );

        const validationType = isIpsMode
          ? SupplierRegistrationProcessFormType.incomingPaymentSecurity
          : SupplierRegistrationProcessFormType.regularSupplierValidation;

        isSuitable = await isSuitableForOpenBanking(supplierRegistrationProcessRequest, validationType);
      }
    } catch (e) {
      Log.event('Error while checking open banking suitable', {
        error: e,
      });
      isSuitable = false;
    } finally {
      setIsRequestLoading(false);
    }

    return isSuitable;
  };

  // Returns whether the registration was actually stored — the open-banking selector
  // gates its "completed" analytics event on this, since the catch below swallows the
  // failure (error modal) instead of rethrowing.
  async function storeValidationProcessRequest(
    companyInfoValues: SupplierRegistrationCompanyInfoFormFields,
    bankAccountValues: SupplierRegistrationBankAccountFormFields,
  ): Promise<boolean> {
    setIsRequestLoading(true);
    // Bounds the progress percent's visible window to this function's own in-flight request --
    // isRequestLoading is shared by several unrelated spinners on this page (pre-registration,
    // the open-banking suitability check, ...), none of which ever set this themselves.
    supplierRegistrationDocumentsStore.setSubmitProgressPercent(undefined);

    try {
      const shouldUseCaptcha = config.featureFlags?.supplierRegistrationCaptcha;

      const captchaToken = shouldUseCaptcha
        ? await supplierRegistrationCustomerDataStore.getCaptchaToken('store-validation')
        : undefined;

      const companyEmail = companyInfoValues.email.trim();

      let processResponse: ReducedSupplierRegistrationProcess;
      const currentAsyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData;

      if (currentAsyncProcessMaskedData?.isResolved() && configuration?.ref) {
        processResponse = await storeSupplierRegistrationProcessRequestFromAsyncInitiateValidation(
          companyInfoValues,
          bankAccountValues,
          configuration?.ref,
          currentAsyncProcessMaskedData.result,
          captchaToken,
        );

        const iFrameMessage = {
          type: 'nsknoxSupplierRegistrationFinishedSuccessfully',
          data: {
            registrationNumber: processResponse.registrationNumber,
            instruction: {
              nextAction: getNextAction(processResponse.displayInstructionType),
            },
          },
        };

        window.parent.postMessage(JSON.stringify(iFrameMessage), '*');
      } else {
        processResponse = await storeRegularValidationProcessRequest(companyInfoValues, bankAccountValues, captchaToken);
      }

      if (shouldUseCaptcha) {
        supplierRegistrationCustomerDataStore.getCaptchaRef()?.current?.reset();
      }

      supplierRegistrationFormStore.cleanSupplierRegistrationStoreValues();

      browserHistory.push(
        navigationStore.generateAccountValidationAfterForm(
          false,
          companyEmail,
          null,
          configuration?.ref ?? null,
          processResponse.displayInstructionType,
          supplierRegistrationFormStore.skipWelcome,
        ),
      );

      return true;
    } catch (e) {
      if (handleDocumentSubmissionSpecificError(e)) {
        return false;
      }

      const errorLogId = extractLogErrorIdFromError(e);

      const bodyContent = (
        <div>
          <Trans i18nKey='supplierValidation.supplierRegister.errors.anErrorOccurredDuringTheRegistration'>
            An error occurred during the registration. Please contact us at <BlueText>accountvalidation@nsknox.net</BlueText> for
            assistance.
          </Trans>
          {errorLogId && (
            <>
              <br />
              <Trans i18nKey='supplierValidation.supplierRegister.errors.referToErrorCode' values={{ errorLogId }}>
                Refer to error code: <LtrText>{'{{ errorLogId }}'}</LtrText>
              </Trans>
            </>
          )}
        </div>
      );

      showContentOnlyModal((onDone) => (
        <ErrorModal
          headerContent={t('general.somethingWentWrong')}
          bodyContent={bodyContent}
          okButtonText={t('general.ok')}
          onDone={onDone}
        />
      ));

      return false;
    } finally {
      if (isMountedRef.current) {
        setIsRequestLoading(false);
      }
      supplierRegistrationDocumentsStore.setSubmitProgressPercent(undefined);
    }
  }

  // Handles the error classes unique to the multipart with-documents endpoints (see Task 5's
  // MorteeExceptionHandler): the three dedicated WebErrorCodes, and the 422 per-file rejection body.
  // Returns false for anything else (including a plain ConstraintViolation 400, whose `error` is
  // null -- see WebCommonExceptionHandler -- so it was never handled specially even before this
  // endpoint existed), leaving it to the existing generic error modal.
  function handleDocumentSubmissionSpecificError(e: unknown): boolean {
    if (!isRequestError(e)) {
      return false;
    }

    if (isWebErrorContent(e.responseJSON)) {
      const i18nKey = getDocumentSubmissionLimitErrorI18nKey(e.responseJSON.error);

      if (i18nKey) {
        // EMAIL_MISMATCH means the guest identity itself is wrong for this registration (only
        // reachable when an async-prefilled email differs from the one the supplier verified).
        // The mismatched session must be dropped, or the context-scoped restore would re-verify
        // the same wrong email forever and the verification modal would never reopen. Stepping
        // back to companyInformation puts the supplier where the re-verification gate lives.
        if (e.responseJSON.error === 'EMAIL_MISMATCH') {
          guestSession.clearSession();
          supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.companyInformation);
        }

        showContentOnlyModal((onDone) => (
          <ErrorModal
            headerContent={t('general.somethingWentWrong')}
            bodyContent={t(i18nKey)}
            okButtonText={t('general.ok')}
            onDone={onDone}
          />
        ));
        return true;
      }
    }

    if (e.code === HttpStatus.unprocessableEntity && isSupplierValidationDocumentsRejectedResponse(e.responseJSON)) {
      supplierRegistrationDocumentsStore.attachServerFileErrors(mapDocumentFileErrorsToTypeAndIndex(e.responseJSON));
      // The attached errors render on the DocumentUploadSection, which lives on the
      // companyInformation step -- the user is on bankAccountDetails when submit fails, so without
      // this they'd have no way to discover which files were rejected.
      supplierRegistrationFormStore.setValidationProcessStep(ValidationProcessSteps.companyInformation);
      return true;
    }

    return false;
  }

  async function storeRegularValidationProcessRequest(
    companyInfoValues: SupplierRegistrationCompanyInfoFormFields,
    bankAccountValues: SupplierRegistrationBankAccountFormFields,
    captchaToken: string | undefined,
  ): Promise<ReducedSupplierRegistrationProcess> {
    const googleAnalyticsClientId: string | null = getGoogleAnalyticsClientIdFromCookie();

    const payload = transformRegularValidationProcessRequest(
      companyInfoValues,
      bankAccountValues,
      isIpsMode,
      configuration,
      supplierRegistrationFormStore.isIndividual,
      googleAnalyticsClientId,
    );

    if (supplierRegistrationDocumentsStore.hasAnyValidFiles) {
      return await supplierRegistrationFormStore.storeValidationProcessRequestWithDocuments(
        payload,
        supplierRegistrationDocumentsStore.validFilesByType,
        isIpsMode,
        languageStore.selectedLocale,
        captchaToken,
        supplierRegistrationDocumentsStore.setSubmitProgressPercent,
      );
    }

    return await supplierRegistrationFormStore.storeValidationProcessRequest(
      payload,
      isIpsMode,
      languageStore.selectedLocale,
      captchaToken,
    );
  }

  async function storeSupplierRegistrationProcessRequestFromAsyncInitiateValidation(
    companyInfoValues: SupplierRegistrationCompanyInfoFormFields,
    bankAccountValues: SupplierRegistrationBankAccountFormFields,
    ref: string,
    asyncMaskedData: AsyncInitiateValidationMaskedData,
    captchaToken: string | undefined,
  ): Promise<ReducedSupplierRegistrationProcess> {
    const googleAnalyticsClientId: string | null = getGoogleAnalyticsClientIdFromCookie();

    const payload = transformFormFieldsToSupplierRegistrationAsyncRequest(
      companyInfoValues,
      bankAccountValues,
      ref,
      asyncMaskedData,
      supplierRegistrationFormStore.isIndividual,
      googleAnalyticsClientId,
    );

    if (supplierRegistrationDocumentsStore.hasAnyValidFiles) {
      return await supplierRegistrationFormStore.storeSupplierRegistrationProcessRequestFromAsyncInitiateValidationWithDocuments(
        payload,
        supplierRegistrationDocumentsStore.validFilesByType,
        asyncMaskedData.id,
        languageStore.selectedLocale,
        captchaToken,
        supplierRegistrationDocumentsStore.setSubmitProgressPercent,
      );
    }

    return await supplierRegistrationFormStore.storeSupplierRegistrationProcessRequestFromAsyncInitiateValidation(
      payload,
      asyncMaskedData.id,
      languageStore.selectedLocale,
      captchaToken,
    );
  }

  return (
    <TransitionLoader
      loading={isRequestLoading}
      label={
        supplierRegistrationDocumentsStore.submitProgressPercent !== undefined
          ? `${Math.round(supplierRegistrationDocumentsStore.submitProgressPercent)}%`
          : undefined
      }
    >
      <PageWithHeader paddingTop='none' widthReactive>
        <PageContainer>
          <AboveCardSpace>
            {renderBackButton()}
            {supplierRegistrationFormStore.currentValidationProcessStep === ValidationProcessSteps.welcome && (
              <LogoCarousel logos={additionalLogos} maxLogosToKeepStatic={5} />
            )}
          </AboveCardSpace>
          {configuration?.ref && (config.supplierRegistration.customersWithoutSubLogo ?? []).includes(configuration.ref) && (
            <SubLogoContainer>
              <PoweredBy
                accessibilityLabel={t('general.accessibility.poweredBy')}
                data-testid='powered-by-logo'
                image={PoweredByLogo}
              />
              <NsknoxRefLogo accessibilityLabel='nsKnox' data-testid='nsknox-logo' image={nsknoxLogoBlack} />
            </SubLogoContainer>
          )}
          {getValidationStepComponent(supplierRegistrationFormStore.currentValidationProcessStep)}
        </PageContainer>
      </PageWithHeader>
    </TransitionLoader>
  );
});

export default SupplierRegistrationFormPage;

const SubLogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const PoweredBy = styled(SVG)`
  height: 17px;
  margin-right: 10px;
  fill: currentcolor;
`;

const NsknoxRefLogo = styled(SVG)`
  height: 21px;
  margin-bottom: 10px;
`;
